import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Lock, Unlock, Fingerprint, ScanFace, KeyRound, Eye, EyeOff,
  CheckCircle2, AlertCircle, ArrowLeft, Smartphone, RefreshCw,
  Grid3X3
} from 'lucide-react';
import { useApplications, APP_ICON_MAP } from '../hooks/useApplications';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from './MainLayout';
import appSecurityService, { ALL_CIPHERVAULT_APPS } from '../security/AppSecurityService';
import { verifyCredential } from '../security/AppSecurityCrypto';
import PinKeypad from './security/PinKeypad';
import PatternLockCanvas from './security/PatternLockCanvas';

export default function AppRouteGuard({ slug, children }) {
  const { applications, loading, hasPermission } = useApplications();
  const {
    user,
    reauthenticateWithMasterPassword,
    unlockVaultWithBiometrics,
    reauthenticateWithPasskey
  } = useAuth();
  const navigate = useNavigate();

  // Find app metadata
  const app = useMemo(() => {
    const fromApi = applications.find((a) => a.slug === slug);
    if (fromApi) return fromApi;
    const fallback = ALL_CIPHERVAULT_APPS.find((a) => a.slug === slug);
    return fallback ? { ...fallback, enabled: true } : null;
  }, [applications, slug]);

  // Read per-app security configuration (Default = OFF)
  const [appConfig, setAppConfig] = useState(() =>
    appSecurityService.getAppConfig(user?.id, slug)
  );

  // Lock status calculation
  const [isAppLocked, setIsAppLocked] = useState(() => {
    if (!appSecurityService.isAppProtected(user?.id, slug)) {
      return false; // Default OFF
    }
    return !appSecurityService.isAppUnlocked(user?.id, slug);
  });

  // Re-sync config and lock state on user change or security update
  useEffect(() => {
    const syncLockState = () => {
      const config = appSecurityService.getAppConfig(user?.id, slug);
      setAppConfig(config);
      if (!config.enabled) {
        setIsAppLocked(false);
      } else {
        const unlocked = appSecurityService.isAppUnlocked(user?.id, slug);
        setIsAppLocked(!unlocked);
      }
    };

    syncLockState();

    window.addEventListener('app-security-updated', syncLockState);
    window.addEventListener('app-locked', syncLockState);
    window.addEventListener('all-apps-locked', syncLockState);
    return () => {
      window.removeEventListener('app-security-updated', syncLockState);
      window.removeEventListener('app-locked', syncLockState);
      window.removeEventListener('all-apps-locked', syncLockState);
    };
  }, [user?.id, slug]);

  // Periodic and visibility check for auto-lock timeout
  useEffect(() => {
    if (!isAppLocked && appConfig.enabled && appConfig.autoLockTimeout > 0) {
      const interval = setInterval(() => {
        const unlocked = appSecurityService.isAppUnlocked(user?.id, slug);
        if (!unlocked) {
          setIsAppLocked(true);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAppLocked, appConfig, user?.id, slug]);

  // Background tab visibility listener
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && appConfig.enabled && appConfig.autoLockTimeout === 0) {
        // Immediate lock on blur/exit
        appSecurityService.lockApp(slug);
        setIsAppLocked(true);
      } else if (!document.hidden && appConfig.enabled) {
        const unlocked = appSecurityService.isAppUnlocked(user?.id, slug);
        if (!unlocked) {
          setIsAppLocked(true);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [appConfig, user?.id, slug]);

  // WebAuthn / Native Biometric capability detection
  const [biometricAvailable, setBiometricAvailable] = useState(null);
  const [biometricLabel, setBiometricLabel] = useState('Biometrics');

  useEffect(() => {
    let cancelled = false;
    async function detectBiometric() {
      try {
        if (!window.PublicKeyCredential) {
          if (!cancelled) setBiometricAvailable(false);
          return;
        }
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        if (!cancelled) {
          setBiometricAvailable(available);
          if (available) {
            const ua = navigator.userAgent.toLowerCase();
            const isIOS = /iphone|ipad|ipod/.test(ua);
            const isMac = /mac/.test(ua) && !isIOS;
            const isAndroid = /android/.test(ua);
            if (isIOS || isMac) setBiometricLabel('Face ID / Touch ID');
            else if (isAndroid) setBiometricLabel('Fingerprint / Face Unlock');
            else setBiometricLabel('Windows Hello / Passkey');
          }
        }
      } catch {
        if (!cancelled) setBiometricAvailable(false);
      }
    }
    detectBiometric();
    return () => { cancelled = true; };
  }, []);

  // Compute available auth modes based on configuration
  const availableModes = useMemo(() => {
    const modes = [];
    if (appConfig.methods?.biometric) modes.push('biometric');
    if (appConfig.methods?.pin && appConfig.pinConfig) modes.push('pin');
    if (appConfig.methods?.pattern && appConfig.patternConfig) modes.push('pattern');
    if (appConfig.methods?.password) modes.push('password');
    // Fallback to password if no methods configured
    if (modes.length === 0) modes.push('password');
    return modes;
  }, [appConfig]);

  // Determine current active authentication mode
  const [authMode, setAuthMode] = useState(() => {
    const primary = appConfig.primaryMethod || 'biometric';
    if (primary === 'pin' && appConfig.methods?.pin && appConfig.pinConfig) return 'pin';
    if (primary === 'pattern' && appConfig.methods?.pattern && appConfig.patternConfig) return 'pattern';
    if (primary === 'password' && appConfig.methods?.password) return 'password';
    if (primary === 'biometric' && appConfig.methods?.biometric) return 'biometric';
    return availableModes[0] || 'password';
  });

  // Keep authMode in sync if config changes
  useEffect(() => {
    if (!availableModes.includes(authMode)) {
      setAuthMode(availableModes[0] || 'password');
    }
  }, [availableModes, authMode]);

  // Unlock state & feedback
  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [scanError, setScanError] = useState('');
  const [unlockPassword, setUnlockPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const hasAutoScannedRef = useRef(false);

  // Trigger Biometric Scan
  const triggerBiometricScan = async () => {
    if (isScanning || scanSuccess) return;
    setIsScanning(true);
    setScanError('');
    setScanSuccess(false);
    if (navigator.vibrate) navigator.vibrate(30);

    try {
      const unlocked = await unlockVaultWithBiometrics(false);
      if (unlocked) {
        handleUnlockSuccess();
        return;
      }
      const passkeyOk = await reauthenticateWithPasskey();
      if (passkeyOk) {
        handleUnlockSuccess();
        return;
      }
      setIsScanning(false);
      setScanError(`${biometricLabel} verification failed or was cancelled. Please use your master password.`);
    } catch (err) {
      setIsScanning(false);
      setScanError(err?.message || 'Authentication failed. Please use your master password.');
    }
  };

  // Auto-scan biometrics once when arriving at locked screen
  useEffect(() => {
    if (isAppLocked && authMode === 'biometric' && biometricAvailable === true && !hasAutoScannedRef.current) {
      hasAutoScannedRef.current = true;
      const timer = setTimeout(() => { triggerBiometricScan(); }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAppLocked, authMode, biometricAvailable]);

  // Unlock success helper
  const handleUnlockSuccess = () => {
    setScanSuccess(true);
    setIsScanning(false);
    setScanError('');
    if (navigator.vibrate) navigator.vibrate([40, 60, 100]);
    appSecurityService.markAppUnlocked(slug);
    setTimeout(() => {
      setIsAppLocked(false);
      setScanSuccess(false);
    }, 650);
  };

  // Handle Master Password Unlock
  const handlePasswordUnlock = async (e) => {
    e?.preventDefault();
    if (!unlockPassword) {
      setScanError('Please enter your master password.');
      return;
    }
    setUnlocking(true);
    setScanError('');
    try {
      await reauthenticateWithMasterPassword(unlockPassword);
      handleUnlockSuccess();
    } catch {
      setScanError('Incorrect master password. Please verify and try again.');
    } finally {
      setUnlocking(false);
    }
  };

  // Handle PIN Unlock
  const handlePinUnlock = async (enteredPin) => {
    setScanError('');
    if (!appConfig.pinConfig?.salt || !appConfig.pinConfig?.hash) {
      setScanError('PIN is not configured properly.');
      return;
    }
    setUnlocking(true);
    try {
      const ok = await verifyCredential(enteredPin, appConfig.pinConfig.salt, appConfig.pinConfig.hash);
      if (ok) {
        handleUnlockSuccess();
      } else {
        if (navigator.vibrate) navigator.vibrate([80, 50, 80]);
        setScanError('Incorrect PIN. Please try again.');
      }
    } catch {
      setScanError('PIN verification failed.');
    } finally {
      setUnlocking(false);
    }
  };

  // Handle Pattern Unlock
  const handlePatternUnlock = async (patternNodes) => {
    setScanError('');
    if (!appConfig.patternConfig?.salt || !appConfig.patternConfig?.hash) {
      setScanError('Pattern is not configured properly.');
      return;
    }
    setUnlocking(true);
    try {
      const ok = await verifyCredential(patternNodes, appConfig.patternConfig.salt, appConfig.patternConfig.hash);
      if (ok) {
        handleUnlockSuccess();
      } else {
        if (navigator.vibrate) navigator.vibrate([80, 50, 80]);
        setScanError('Incorrect pattern. Please try again.');
      }
    } catch {
      setScanError('Pattern verification failed.');
    } finally {
      setUnlocking(false);
    }
  };

  // Permission / loading check
  if (loading) {
    return (
      <MainLayout>
        <div style={{ display: 'grid', placeItems: 'center', height: '60vh' }}>
          <p style={{ color: 'var(--text-secondary)' }}>Verifying application authorization...</p>
        </div>
      </MainLayout>
    );
  }

  // Permission access check
  if (app && app.enabled === false) {
    return <Navigate to="/access-denied" replace />;
  }

  if (hasPermission && !hasPermission(slug, 'access')) {
    return <Navigate to="/access-denied" replace />;
  }

  // ══════════════════════════════════════════════════════════
  // RENDER APP PROTECTED SCREEN MATCHING REFERENCE DESIGN
  // ══════════════════════════════════════════════════════════
  if (isAppLocked) {
    const appName = app?.name || 'Application';

    return (
      <MainLayout hideDock={true} hideSidebar={true}>
        <div className="ios-app-lock-container animate-fade-in">
          <div className="ios-app-lock-card animate-scale-up">

            {/* Top Badges: App Protected & Device Auth */}
            <div className="v-lock-header" style={{ marginBottom: '14px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className={`v-lock-badge ${scanSuccess ? 'unlocked' : 'locked'}`}>
                {scanSuccess ? <Unlock size={13} className="v-lock-icon-unlocked" /> : <Lock size={13} className="v-lock-icon-locked" />}
                <span>{scanSuccess ? 'Unlocked' : 'App Protected'}</span>
              </div>
              <div className="v-lock-os-badge">
                <Smartphone size={11} />
                <span>Device Auth</span>
              </div>
            </div>

            {/* App Card Header: Lock Icon Bubble + App Name + Subtitle */}
            <div className="ios-app-badge-row">
              <div className="ios-app-icon-bubble">
                <Lock size={22} color="#059669" />
              </div>
              <div className="ios-app-info-col">
                <h2 className="ios-app-lock-title">{appName}</h2>
                <p className="ios-app-lock-subtitle">Zero-Knowledge Encrypted</p>
              </div>
            </div>

            {/* Segmented Auth Mode Switcher (shows all enabled methods) */}
            {availableModes.length > 1 && (
              <div className="ios-auth-mode-row" style={{ marginBottom: '14px', width: '100%' }}>
                {availableModes.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={`ios-auth-mode-btn ${authMode === mode ? 'active' : ''}`}
                    onClick={() => {
                      setAuthMode(mode);
                      setScanError('');
                    }}
                  >
                    {mode === 'biometric' && <><Fingerprint size={13} /><span>Biometrics</span></>}
                    {mode === 'pin' && <><span style={{ fontWeight: 700, fontSize: '0.8rem' }}>#</span><span>PIN</span></>}
                    {mode === 'pattern' && <><Grid3X3 size={13} /><span>Pattern</span></>}
                    {mode === 'password' && <><KeyRound size={13} /><span>Password</span></>}
                  </button>
                ))}
              </div>
            )}

            {/* ─── 1. BIOMETRICS VIEW ─── */}
            {authMode === 'biometric' && (
              <div className="v-biometric-section animate-fade-in" style={{ width: '100%' }}>
                <div className="native-auth-prompt-box">
                  {scanSuccess ? (
                    <div className="v-scan-success-icon animate-pop-in">
                      <CheckCircle2 size={44} color="#10b981" strokeWidth={2.5} />
                    </div>
                  ) : isScanning ? (
                    <div className="native-auth-scanning">
                      <div className="native-auth-pulse-ring" />
                      <ScanFace size={40} className="native-auth-icon v-pulsing" />
                    </div>
                  ) : (
                    <div
                      className="native-auth-idle"
                      onClick={triggerBiometricScan}
                      role="button"
                      tabIndex={0}
                      title={`Unlock with ${biometricLabel}`}
                      onKeyDown={(e) => e.key === 'Enter' && triggerBiometricScan()}
                    >
                      <Fingerprint size={44} className="native-auth-icon" />
                    </div>
                  )}
                </div>

                <div className="v-scanner-label" style={{ margin: '10px 0 14px' }}>
                  {scanSuccess ? (
                    <span className="v-label-success">Authenticated! Opening {appName}...</span>
                  ) : isScanning ? (
                    <span className="v-label-scanning">
                      <RefreshCw size={13} className="et-spin" />&nbsp;Follow the prompt on your device...
                    </span>
                  ) : (
                    <span className="v-label-prompt">Tap to authenticate with {biometricLabel}</span>
                  )}
                </div>

                {scanError && (
                  <div className="v-scan-error-alert animate-shake">
                    <AlertCircle size={14} /><span>{scanError}</span>
                  </div>
                )}

                <div className="v-biometric-actions">
                  <button
                    type="button"
                    className="v-btn-primary-scan"
                    onClick={triggerBiometricScan}
                    disabled={isScanning || scanSuccess}
                  >
                    {isScanning ? (
                      <><RefreshCw size={14} className="et-spin" /> Authenticating...</>
                    ) : (
                      <><ScanFace size={16} /><span>Use {biometricLabel}</span></>
                    )}
                  </button>

                  {/* Fallback button */}
                  {availableModes.includes('password') && (
                    <button
                      type="button"
                      className="v-btn-switch-auth"
                      onClick={() => { setAuthMode('password'); setScanError(''); }}
                    >
                      <KeyRound size={13} /><span>Use Master Password instead</span>
                    </button>
                  )}
                  {availableModes.includes('pin') && !availableModes.includes('password') && (
                    <button
                      type="button"
                      className="v-btn-switch-auth"
                      onClick={() => { setAuthMode('pin'); setScanError(''); }}
                    >
                      <span>Use PIN instead</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ─── 2. PIN VIEW ─── */}
            {authMode === 'pin' && (
              <div className="v-pin-section animate-fade-in" style={{ width: '100%' }}>
                <PinKeypad
                  pinLength={appConfig.pinConfig?.length || 4}
                  title="Enter Security PIN"
                  subtitle={`Enter your ${appConfig.pinConfig?.length || 4}-digit PIN to unlock ${appName}`}
                  error={scanError}
                  disabled={unlocking || scanSuccess}
                  onComplete={handlePinUnlock}
                />

                {availableModes.includes('biometric') && (
                  <button
                    type="button"
                    className="v-btn-switch-auth"
                    style={{ marginTop: '12px' }}
                    onClick={() => { setAuthMode('biometric'); setScanError(''); }}
                  >
                    <Fingerprint size={13} /><span>Use {biometricLabel} instead</span>
                  </button>
                )}
                {availableModes.includes('password') && (
                  <button
                    type="button"
                    className="v-btn-switch-auth"
                    style={{ marginTop: '6px' }}
                    onClick={() => { setAuthMode('password'); setScanError(''); }}
                  >
                    <KeyRound size={13} /><span>Use Master Password instead</span>
                  </button>
                )}
              </div>
            )}

            {/* ─── 3. PATTERN VIEW ─── */}
            {authMode === 'pattern' && (
              <div className="v-pattern-section animate-fade-in" style={{ width: '100%' }}>
                <PatternLockCanvas
                  title="Draw Pattern"
                  subtitle={`Connect the pattern nodes to unlock ${appName}`}
                  error={scanError}
                  disabled={unlocking || scanSuccess}
                  onComplete={handlePatternUnlock}
                />

                {availableModes.includes('biometric') && (
                  <button
                    type="button"
                    className="v-btn-switch-auth"
                    style={{ marginTop: '12px' }}
                    onClick={() => { setAuthMode('biometric'); setScanError(''); }}
                  >
                    <Fingerprint size={13} /><span>Use {biometricLabel} instead</span>
                  </button>
                )}
                {availableModes.includes('password') && (
                  <button
                    type="button"
                    className="v-btn-switch-auth"
                    style={{ marginTop: '6px' }}
                    onClick={() => { setAuthMode('password'); setScanError(''); }}
                  >
                    <KeyRound size={13} /><span>Use Master Password instead</span>
                  </button>
                )}
              </div>
            )}

            {/* ─── 4. PASSWORD VIEW ─── */}
            {authMode === 'password' && (
              <form onSubmit={handlePasswordUnlock} className="v-password-form animate-fade-in" style={{ width: '100%' }}>
                <div className="v-password-field-wrap">
                  <label htmlFor="ios-app-password" className="ios-input-label">Confirm Master Password</label>
                  <div className="v-ios-input-group">
                    <input
                      id="ios-app-password"
                      type={showPassword ? 'text' : 'password'}
                      className="v-lock-ios-input"
                      placeholder="Enter master password"
                      value={unlockPassword}
                      onChange={(e) => setUnlockPassword(e.target.value)}
                      autoFocus
                      required
                    />
                    <button
                      type="button"
                      className="v-input-eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {scanError && (
                  <div className="v-scan-error-alert animate-shake">
                    <AlertCircle size={14} /><span>{scanError}</span>
                  </div>
                )}

                <button type="submit" className="v-btn-primary-scan" disabled={unlocking || !unlockPassword} style={{ marginTop: '12px' }}>
                  {unlocking ? (
                    <><RefreshCw size={14} className="et-spin" /> Unlocking...</>
                  ) : (
                    <><Unlock size={15} /> Unlock Application</>
                  )}
                </button>

                {availableModes.includes('biometric') && (
                  <button
                    type="button"
                    className="v-btn-switch-auth"
                    onClick={() => { setAuthMode('biometric'); setScanError(''); }}
                  >
                    <ScanFace size={13} /><span>Use {biometricLabel} instead</span>
                  </button>
                )}
                {availableModes.includes('pin') && (
                  <button
                    type="button"
                    className="v-btn-switch-auth"
                    onClick={() => { setAuthMode('pin'); setScanError(''); }}
                  >
                    <span>Use PIN instead</span>
                  </button>
                )}
              </form>
            )}

            {/* Back to Applications */}
            <div className="ios-lock-footer-row">
              <button type="button" className="ios-lock-cancel-btn" onClick={() => navigate('/apps')}>
                <ArrowLeft size={14} /><span>Back to Applications</span>
              </button>
            </div>

            {/* Active User Session Pill */}
            <div className="ios-lock-session-pill">
              <span className="ios-lock-dot-live" />
              <span>Signed in as <strong>{user?.email || 'User'}</strong> • Session Active</span>
            </div>

          </div>
        </div>
      </MainLayout>
    );
  }

  // If unlocked or protection is OFF, render children directly
  return children;
}
